import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageCard } from '../layout/page/PageCard';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { InlineAlert } from '../ui/InlineAlert';
import { RoundedCheckbox } from '../ui/RoundedCheckbox';
import { usePermissions } from '../../contexts/PermissionsContext';
import { canWriteProductCatalog } from './productCatalogAccess';
import {
  SalesProductResourceLink,
  SalesProductResourceLinkItemPayload,
  getProductResources,
  listProductResources,
  replaceProductResources,
} from '../../services/productResourcesApi';
import { CatalogMultiSelectModal, CatalogSelectItem } from './CatalogMultiSelectModal';

type ProductResourcesPanelProps = {
  productId: string;
  readOnly?: boolean;
};

type DraftLink = SalesProductResourceLinkItemPayload & {
  name: string;
  url?: string | null;
};

export function ProductResourcesPanel({ productId, readOnly = false }: ProductResourcesPanelProps) {
  const { canWriteModule } = usePermissions();
  const canWrite = !readOnly && canWriteProductCatalog(canWriteModule);
  const [links, setLinks] = useState<DraftLink[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const linkedIds = useMemo(() => links.map((link) => link.productResourceId), [links]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const productLinks = await getProductResources(productId);
        setLinks(mapLinksToDraft(productLinks));
        setCheckedIds(new Set());
      } catch {
        toast.error('Falha ao carregar recursos do produto.');
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  function mapLinksToDraft(productLinks: SalesProductResourceLink[]): DraftLink[] {
    return productLinks.map((link) => ({
      productResourceId: link.productResourceId,
      name: link.name,
      url: link.url,
      sortOrder: link.sortOrder,
    }));
  }

  function toggleChecked(id: string): void {
    setCheckedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function removeOne(id: string): void {
    if (!canWrite) return;
    setLinks((current) => current.filter((link) => link.productResourceId !== id));
    setCheckedIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }

  function removeSelected(): void {
    if (!canWrite || checkedIds.size === 0) return;
    setLinks((current) => current.filter((link) => !checkedIds.has(link.productResourceId)));
    setCheckedIds(new Set());
  }

  function handleModalConfirm(selected: CatalogSelectItem[]): void {
    if (!canWrite || selected.length === 0) return;
    setLinks((current) => {
      const existing = new Set(current.map((link) => link.productResourceId));
      const additions = selected
        .filter((item) => !existing.has(item.id))
        .map((item, index) => ({
          productResourceId: item.id,
          name: item.title,
          url: item.extra || null,
          sortOrder: current.length + index,
        }));
      return [...current, ...additions];
    });
  }

  async function handleSave(): Promise<void> {
    if (!canWrite) return;
    setSaving(true);
    try {
      const items: SalesProductResourceLinkItemPayload[] = links.map((link, index) => ({
        productResourceId: link.productResourceId,
        sortOrder: index,
      }));
      await replaceProductResources(productId, items);
      toast.success('Recursos do produto atualizados.');
    } catch {
      toast.error('Falha ao salvar recursos do produto.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-secondary-500">Carregando recursos...</p>;
  }

  return (
    <PageCard title="Recursos" subtitle="Materiais e deliverables vinculados ao produto.">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-secondary-600">
          Catálogo em{' '}
          <Link to="/sales/product-resources" className="erp-link">
            Recursos de produto
          </Link>
          .
        </p>
        {canWrite ? (
          <div className="erp-btn-actions">
            <button
              type="button"
              className="erp-btn-danger erp-btn-sm"
              disabled={checkedIds.size === 0}
              onClick={removeSelected}
            >
              Excluir selecionados
            </button>
            <button type="button" className="erp-btn-secondary" onClick={() => setModalOpen(true)}>
              Adicionar recursos
            </button>
          </div>
        ) : null}
      </div>

      {links.length === 0 ? (
        <InlineAlert>Nenhum recurso vinculado. Use “Adicionar recursos”.</InlineAlert>
      ) : (
        <ul className="space-y-2">
          {links.map((link) => (
            <li key={link.productResourceId} className="rounded-lg border border-secondary-200 px-3 py-2">
              <div className="flex items-start gap-3">
                {canWrite ? (
                  <RoundedCheckbox
                    className="mt-1"
                    checked={checkedIds.has(link.productResourceId)}
                    onChange={() => toggleChecked(link.productResourceId)}
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="font-medium text-secondary-900">{link.name}</span>
                      {link.url ? (
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noreferrer"
                          className="erp-link mt-1 block text-sm"
                        >
                          {link.url}
                        </a>
                      ) : null}
                    </div>
                    {canWrite ? (
                      <DeleteIconButton
                        label="Excluir"
                        onClick={() => removeOne(link.productResourceId)}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {canWrite ? (
        <div className="mt-4">
          <button
            type="button"
            className="erp-btn-primary"
            disabled={saving}
            onClick={() => void handleSave()}
          >
            {saving ? 'Salvando...' : 'Salvar recursos'}
          </button>
        </div>
      ) : null}

      <CatalogMultiSelectModal
        open={modalOpen}
        title="Selecionar recursos"
        subtitle="Role para carregar mais itens do catálogo."
        alreadySelectedIds={linkedIds}
        fetchPage={async (page, pageSize, search) => {
          const result = await listProductResources({ activeOnly: true, page, pageSize, search });
          return {
            items: result.items.map((item) => ({
              id: item.id,
              title: item.name,
              subtitle: item.resourceType,
              description: item.description,
              extra: item.url ?? undefined,
            })),
            totalCount: result.totalCount,
            page: result.page,
            pageSize: result.pageSize,
          };
        }}
        onClose={() => setModalOpen(false)}
        onConfirm={handleModalConfirm}
      />
    </PageCard>
  );
}
